import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FolderOpen, FolderPlus, TriangleAlert } from "lucide-react";
import { ApiError } from "../../../../api/fastapi";
import { Button } from "../../../../components/ui/button";
import { useWorkStepImport } from "../../../../contexts/workStepImportContext";
import type { WorkStepCandidate } from "../../../../lib/workStepImport";
import {
  useBulkImportWorkSteps,
  useOpsAudits,
} from "../../../../hooks/useWorkSteps";

// Column mapping is hardcoded product knowledge (see workStepImport.ts) and
// applied silently at upload — there is no mapping UI. This page is the
// grouped-by-OpsAudit review before the rows become work steps.
export const ReviewWorkStepImportPage = () => {
  const navigate = useNavigate();
  const { parsed, fileName, candidates, clear } = useWorkStepImport();
  const { opsAudits } = useOpsAudits();
  const { bulkImportWorkSteps } = useBulkImportWorkSteps();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!parsed) {
      navigate("/audit/work-steps/import", { replace: true });
    }
  }, [parsed, navigate]);

  // Grouped preview: audit label -> candidates. Rows without an audit
  // value stay ungrouped ("No Audit", sorted last); labels that don't
  // match an existing OpsAudit are flagged as new — they're auto-created at
  // import, mirroring how the Optro import will behave. Grouping is
  // case-insensitive (first-seen casing shown) because the backend's
  // find-or-create merges case-variant audit names into one audit.
  const groups = useMemo(() => {
    const byKey = new Map<
      string,
      { label: string; members: WorkStepCandidate[] }
    >();
    for (const candidate of candidates) {
      const label = candidate.opsAuditName || "No Audit";
      const key = label.toLowerCase();
      const group = byKey.get(key) ?? { label, members: [] };
      group.members.push(candidate);
      byKey.set(key, group);
    }
    return [...byKey.values()]
      .map(({ label, members }) => ({
        label,
        members,
        isNew:
          label !== "No Audit" &&
          !opsAudits.some(
            (opsAudit) => opsAudit.name.toLowerCase() === label.toLowerCase(),
          ),
      }))
      .sort((a, b) => {
        if (a.label === "No Audit") return 1;
        if (b.label === "No Audit") return -1;
        return a.label.localeCompare(b.label);
      });
  }, [candidates, opsAudits]);

  // Rows with a blank name would 400 the whole import server-side; surface
  // them in the preview and block import until the file is fixed.
  const blankNameCount = useMemo(
    () => candidates.filter((candidate) => !candidate.name.trim()).length,
    [candidates],
  );

  const onImport = async () => {
    if (candidates.length === 0) {
      toast.error("No rows found to import");
      return;
    }
    setSubmitting(true);
    try {
      const importedCount = await bulkImportWorkSteps(
        candidates.map((candidate) => ({
          name: candidate.name,
          description: candidate.description,
          section: candidate.section,
          subsection: candidate.subsection,
          preparer: candidate.preparer,
          opsAuditName: candidate.opsAuditName,
        })),
      );
      toast.success(
        `Imported ${importedCount} work step${importedCount === 1 ? "" : "s"}`,
      );
      clear();
      navigate("/audit/work-steps");
    } catch (error) {
      console.error("Failed to import work steps:", error);
      toast.error(
        error instanceof ApiError
          ? error.getUserMessage()
          : "Failed to import work steps",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full overflow-y-auto">
      <div className="p-10 max-w-[1100px] mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold tracking-tight">Review Import</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            We've read {fileName || "your file"} and organized the work steps by
            audit. Audits that don't exist yet will be created automatically.
            Review below, then import.
          </p>
        </div>

        {/* Preview grouped by OpsAudit */}
        <div className="border rounded-md overflow-hidden">
          <div className="p-3 border-b text-sm font-medium bg-white">
            {candidates.length} work step
            {candidates.length === 1 ? "" : "s"} in {groups.length} group
            {groups.length === 1 ? "" : "s"}
          </div>
          <div className="relative max-h-[calc(100vh-300px)] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white z-10">
                <tr className="border-b">
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[280px] bg-white">
                    Work Step Name
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground bg-white">
                    Description
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[160px] bg-white">
                    Section
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[160px] bg-white">
                    Subsection
                  </th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground w-[180px] bg-white">
                    Preparer
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => [
                  <tr
                    key={`group-${group.label}`}
                    className="border-b bg-stone-100/80"
                  >
                    <td colSpan={5} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {group.isNew ? (
                          <FolderPlus className="h-4 w-4 text-stone-500" />
                        ) : (
                          <FolderOpen className="h-4 w-4 text-stone-500" />
                        )}
                        <span className="font-semibold text-stone-800">
                          {group.label}
                        </span>
                        {group.isNew && (
                          <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-green-700 border border-green-200">
                            will be created
                          </span>
                        )}
                        <span className="ml-auto text-xs text-stone-500">
                          {group.members.length} work step
                          {group.members.length === 1 ? "" : "s"}
                        </span>
                      </div>
                    </td>
                  </tr>,
                  ...group.members.map((candidate, idx) => (
                    <tr
                      key={`${group.label}-${idx}`}
                      className="border-b transition-colors hover:bg-muted/50"
                    >
                      <td className="p-4 align-middle font-medium">
                        {candidate.name.trim() ? (
                          candidate.name
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-amber-700">
                            <TriangleAlert className="h-3.5 w-3.5" />
                            Missing name
                          </span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-stone-700">
                        <div className="line-clamp-2">
                          {candidate.description || "-"}
                        </div>
                      </td>
                      <td className="p-4 align-middle text-stone-700">
                        {candidate.section || "-"}
                      </td>
                      <td className="p-4 align-middle text-stone-700">
                        {candidate.subsection || "-"}
                      </td>
                      <td className="p-4 align-middle text-stone-700">
                        {candidate.preparer || "-"}
                      </td>
                    </tr>
                  )),
                ])}
                {candidates.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="text-center py-8 text-stone-500 text-sm"
                    >
                      No preview rows available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          {blankNameCount > 0 && (
            <p className="mr-auto text-sm text-amber-700">
              {blankNameCount} row{blankNameCount === 1 ? "" : "s"} missing a
              work step name — fix the file and re-upload to import.
            </p>
          )}
          <Button
            variant="outline"
            onClick={() => navigate("/audit/work-steps/import")}
          >
            Back
          </Button>
          <Button
            onClick={onImport}
            disabled={
              candidates.length === 0 || blankNameCount > 0 || submitting
            }
            loading={submitting}
          >
            Import Work Steps
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewWorkStepImportPage;
